import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { Temporal } from 'temporal-polyfill';
import { consumeDOMEvent } from '../utils.js';

function sortDates(date1: Temporal.PlainDate, date2: Temporal.PlainDate) {
  return Temporal.PlainDate.compare(date1, date2) < 0 ? [date1, date2] : [date2, date1];
}

function clampDate(date: Temporal.PlainDate, min: Temporal.PlainDate|undefined, max: Temporal.PlainDate|undefined): Temporal.PlainDate {
  if (min && Temporal.PlainDate.compare(date, min) < 0)
    date = min;
  if (max && Temporal.PlainDate.compare(date, max) > 0)
    date = max;
  return date;
}

type DayOfMonth = {
  dayOfMonth: number,
  dayOfWeek: number,
  relativeWeek: number,
  date: Temporal.PlainDate,
  outside: boolean,
}

function daysInAMonth(anyDateInMonth: Temporal.PlainDate): DayOfMonth[] {
  let fromDate = anyDateInMonth.with({ day: 1 });
  let toDate = fromDate.add({ months: 1 }).subtract({ days: 1}); // the last day of month
  // Offset to the start-of-week.
  fromDate = fromDate.subtract({ days: fromDate.dayOfWeek % 7 })
  // Offset to the end-of-week
  toDate = toDate.add({ days: 6 - (toDate.dayOfWeek % 7) });

  let weekCounter = 0;
  const days: DayOfMonth[] = [];
  for (let d = fromDate; Temporal.PlainDate.compare(d, toDate) <= 0; d = d.add({ days: 1 })) {
    days.push({
      dayOfMonth: d.day,
      dayOfWeek: d.dayOfWeek % 7,
      relativeWeek: d.dayOfWeek === 6 ? weekCounter++ : weekCounter,
      date: d,
      outside: d.month !== anyDateInMonth.month,
    });
  }
  return days;
}

@customElement('calendar-range')
export class CalendarRange extends LitElement {
  @property({ attribute: false }) tz?: string;
  @property({ attribute: false }) since?: Temporal.PlainDate;
  @property({ attribute: false }) until?: Temporal.PlainDate;
  @property({ attribute: false }) min?: Temporal.PlainDate;
  @property({ attribute: false }) max?: Temporal.PlainDate;
  @property({ attribute: false }) maxRangeDays?: number;

  @state() private _isSelecting?: boolean;
  @state() private _selectionStart?: Temporal.PlainDate;
  @state() private _selectionEnd?: Temporal.PlainDate;

  // This is the right of the months.
  @state() private _position?: Temporal.PlainDate;

  protected willUpdate(p: PropertyValues): void {
    super.willUpdate(p);
    if (!this._position) {
      if (this.until)
        this._position = this.until.with({ day: 1 });
      else
        this._position = Temporal.Now.plainDateISO(this.tz);
      return;
    }
  }

  cancelSelection() {
    this._selectionStart = undefined;
    this._selectionEnd = undefined;
    this._isSelecting = undefined;
  }

  revealDate(date: Temporal.PlainDate) {
    if (!this._position) {
      this._position = date.with({ day: 1 });
      return;
    }

    const focusMonth = date.with({ day: 1 });
    const focusNextMonth = date.with({ day: 1 }).add({ months: 1 });
    if (Temporal.PlainDate.compare(focusNextMonth, this._position) === 0 || Temporal.PlainDate.compare(focusMonth, this._position) === 0) {
      // do nothing. it fits in the current view.
    } else if (Temporal.PlainDate.compare(focusMonth, this._position) < 0) {
      // scroll to the left
      this._position = focusMonth;
    } else {
      // scroll to the right
      this._position = focusMonth;
    }
  }

  disconnectedCallback(): void {
    this._selectionStart = undefined;
    this._selectionEnd = undefined;
    this._isSelecting = undefined;
  }

  override render() {
    if (!this._position)
      return nothing;
    let min = this.min;
    let max = this.max;

    if (this.maxRangeDays && this._isSelecting && this._selectionStart) {
      min = clampDate(this._selectionStart.add({ days: -this.maxRangeDays + 1 }), this.min, this.max);
      max = clampDate(this._selectionStart.add({ days: this.maxRangeDays - 1 }), this.min, this.max);
    }

    let since: Temporal.PlainDate|undefined;
    let until: Temporal.PlainDate|undefined;
    if (this._isSelecting) {
      [since, until] = sortDates(this._selectionStart!, this._selectionEnd!);
    } else {
      since = this.since;
      until = this.until;
    }
    if (since)
      since = clampDate(since, min, max);
    if (until)
      until = clampDate(until, min, max);

    const date2 = this._position;
    const date1 = date2.with({ day: 1 }).subtract({ months: 1 });

    const year1 = date1.year;
    const year2 = date2.year;

    const prevFocusDate = this._position.with({ day: 1 }).subtract({ months: 1 });
    const nextFocusDate = this._position.with({ day: 1 }).add({ months: 1 });

    return html`
      <v-box>
        <h-box>
          <sl-icon-button ?disabled=${!!min && Temporal.PlainDate.compare(min, prevFocusDate) > 0} @click=${(event: MouseEvent) => {
            this._position = prevFocusDate;
            consumeDOMEvent(event);
          }} name=chevron-left></sl-icon-button>
          <x-filler></x-filler>
          <span class=year-title>${year1 === year2 ? html`${year1}` : html`${year1}–${year2}`}</span>
          <x-filler></x-filler>
          <sl-icon-button ?disabled=${!!max && Temporal.PlainDate.compare(max, nextFocusDate) < 0} @click=${(event: MouseEvent) => {
            this._position = nextFocusDate;
            consumeDOMEvent(event);
          }} name=chevron-right></sl-icon-button>
        </h-box>
        <h-box style="align-items: start; justify-content: start;">
          ${this._renderMonth(date1, min, max, since, until)}
          ${this._renderMonth(date2, min, max, since, until)}
        </h-box>
      </v-box>
    `;
  }

  private _dayClicked(day: Temporal.PlainDate) {
    if (!this._isSelecting) {
      this._isSelecting = true;
      this._selectionStart = day;
      this._selectionEnd = day;
      this.dispatchEvent(new CustomEvent<Temporal.PlainDate>('rangestart', {
        detail: day,
      }));
      if (this.maxRangeDays !== 1)
        return;
    }
    [this.since, this.until] = sortDates(this._selectionStart!, day);
    this._selectionStart = undefined;
    this._selectionEnd = undefined;
    this._isSelecting = false;
    this.dispatchEvent(new CustomEvent<Temporal.PlainDate>('rangeend', {
      detail: day,
    }));
  }

  private _dayHovered(day: Temporal.PlainDate) {
    this._selectionEnd = day;
  }

  private _renderDay(day: DayOfMonth, min: Temporal.PlainDate | undefined, max: Temporal.PlainDate | undefined, since: Temporal.PlainDate | undefined, until: Temporal.PlainDate | undefined) {
    const isDisabled = (!!min && Temporal.PlainDate.compare(day.date, min) < 0) ||
        (!!max && Temporal.PlainDate.compare(day.date, max) > 0);

    const today = Temporal.Now.plainDateISO(this.tz);
    const isToday = Temporal.PlainDate.compare(today, day.date) === 0;

    const render = html`
      <span class=${classMap({
        day: true,
        disabled: isDisabled,
        'outside-month': day.outside,
        rstart: !!since && Temporal.PlainDate.compare(since, day.date) === 0,
        rend: !!until && Temporal.PlainDate.compare(day.date, until) === 0,
        'is-today': isToday,
        rinside: !!since && !!until
          && Temporal.PlainDate.compare(since, day.date) <= 0
          && Temporal.PlainDate.compare(day.date, until) <= 0,
      })} style=${styleMap({
        'grid-column': day.dayOfWeek + 1,
        'grid-row': day.relativeWeek + 3,
      })}
        @click=${isDisabled ? nothing : (e: MouseEvent) => {
          this._dayClicked(day.date);
          consumeDOMEvent(e);
        }}
        @mouseover=${isDisabled ? nothing : this._isSelecting ? (e: MouseEvent) => {
          this._dayHovered(day.date);
          consumeDOMEvent(e);
        } : nothing}
      ><span class=day-text>${day.dayOfMonth}</span></span>
    `;
    if (isToday && !this._isSelecting) {
      const text: string[] = [];
      if (isToday)
        text.push('Today');
      return html`<sl-tooltip content=${text.join('\n')}>${render}</sl-tooltip>`;
    }
    return render;
  }

  private _renderMonth(date: Temporal.PlainDate, min: Temporal.PlainDate | undefined, max: Temporal.PlainDate | undefined, since: Temporal.PlainDate | undefined, until: Temporal.PlainDate | undefined) {
    const days = daysInAMonth(date);
    return html`
      <section class=month>
        <span class=month-title>${date.toLocaleString('en-US', { month: 'long' })}</span>
        ${['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((t, index) => html`<span class=${classMap({
          'day-title': true,
          day: true,
          weekend: index === 0 || index === 6,
        })}>${t}</span>`)}
        ${days.map(day => this._renderDay(day, min, max, since, until))}
      </section>
    `;
  }

  static styles = [css`
    :host {
      display: flex;
    }
    .month {
      display: inline-grid;
      grid-template-columns: auto;
      grid-template-rows: auto;
      row-gap: var(--sl-spacing-3x-small);

      .month-title {
        grid-row: 1;
        grid-column: 1/8;
        align-self: center;
        justify-self: center;
        color: var(--sl-color-neutral-700);
        font-weight: var(--sl-font-weight-bold);
        font-size: var(--sl-font-size-small);
      }
    }

    .year-title {
      font-size: var(--sl-font-size-large);
    }
    
    .day-title {
      grid-row: 2;
      font-weight: var(--sl-font-weight-bold);
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-700);
      border-bottom: 1px solid var(--fk-color-border);

      &.weekend {
        color: #af0000;
      }
    }

    .day {
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--sl-spacing-x-small);
      cursor: pointer;

      &.outside-month {
        color: var(--sl-color-neutral-300);
      }

      &.is-today {
        position: relative;
      }

      &.is-today::after {
        content: "";
        position: absolute;
        inset: 10%;
        border: 3px double var(--fk-color-border);
        border-radius: var(--sl-border-radius-small);
      }

      &.disabled {
        color: var(--sl-color-neutral-300);
        background-color: var(--sl-color-neutral-50);
        cursor: not-allowed;
      }
      
      &.rinside {
        background-color: var(--sl-color-primary-600);
        color: white;
      }

      &.rstart {
        background-color: var(--sl-color-primary-700);
        border-top-left-radius: var(--sl-border-radius-medium);
        border-bottom-left-radius: var(--sl-border-radius-medium);
      }

      &.rend {
        background-color: var(--sl-color-primary-700);
        border-top-right-radius: var(--sl-border-radius-medium);
        border-bottom-right-radius: var(--sl-border-radius-medium);
      }

      .day-text {
        position: relative;
      }
    }

    sl-icon-button {
      border: 1px solid var(--fk-color-border);
      background-color: #fff;
      border-radius: 3px;
    }
  `]
}